/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { GeneralModal, ModalButton } from '../index';
import {
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Alert,
  TextField,
} from '@mui/material';
import React, { useState, useRef } from 'react';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import {
  MAX_LUNCH_START_OFFSET_HOURS,
  lunchStartOffsetHours,
} from '../../utilities/lunchWindow';

export const WorkSetupModal = (props) => {
  const [workSetup, setWorkSetup] = useState('');
  const [lunchStartDate, setLunchStartDate] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prevent multiple clicks
  const confirmTimer = useRef(null);

  const handleWorkSetupChange = (event) => {
    setWorkSetup(event.target.value);
    setError('');
  };

  const handleConfirm = () => {
    // Check if already submitting
    if (isSubmitting || loading) {
      return;
    }

    if (confirmTimer.current) {
      clearTimeout(confirmTimer.current);
    }

    // Set delay
    confirmTimer.current = setTimeout(() => {
      if (!workSetup) {
        setError('Please select a work setup option.');
        return;
      }

      if (workSetup === 'Deployed') {
        if (!lunchStartDate) {
          setError('Please set your lunch break start time.');
          return;
        }
        // A time at or before check-in is read as the next day (overnight
        // shift), so bound how far out it can land -- otherwise a mistyped
        // morning time silently moves lunch outside the shift.
        if (
          lunchStartOffsetHours(new Date(), lunchStartDate) >
          MAX_LUNCH_START_OFFSET_HOURS
        ) {
          setError(
            `Lunch break must start within ${MAX_LUNCH_START_OFFSET_HOURS} hours of your check-in time.`
          );
          return;
        }
      }

      setError('');
      setIsSubmitting(true);
      setLoading(true);

      if (props.onConfirm) {
        props.onConfirm(
          workSetup,
          workSetup === 'Deployed' ? format(lunchStartDate, 'HH:mm') : ''
        );

        // Reset submitting state after a delay
        setTimeout(() => {
          setIsSubmitting(false);
          setLoading(false);
        }, 1000);
      }
    }, 300);
  };

  // HANDLER FOR CANCELLATION
  const handleCancel = () => {
    if (confirmTimer.current) {
      clearTimeout(confirmTimer.current);
    }

    setWorkSetup('');
    setLunchStartDate(null);
    setError('');
    setIsSubmitting(false);
    setLoading(false);

    if (props.onCancel) {
      props.onCancel();
    }
  };

  // HANDLER FOR CLOSE
  const handleClose = () => {
    if (confirmTimer.current) {
      clearTimeout(confirmTimer.current);
    }

    setWorkSetup('');
    setLunchStartDate(null);
    setError('');
    setIsSubmitting(false);
    setLoading(false);

    if (props.onClose) {
      props.onClose();
    }
  };

  // Cleanup timeout
  React.useEffect(() => {
    return () => {
      if (confirmTimer.current) {
        clearTimeout(confirmTimer.current);
      }
    };
  }, []);

  return (
    <GeneralModal
      style={{ width: '30%', display: 'flex', flexDirection: 'column' }}
      open={props.open}
      onClose={handleClose}
    >
      <Typography align="left" sx={{ mb: 1, fontWeight: 600 }} variant="h5">
        Work Setup
      </Typography>
      <Typography
        align="left"
        sx={{ mb: 3, fontWeight: 300, fontStyle: 'italic' }}
        variant="body1"
      >
        Please choose your work setup
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, gap: 8 }}>
        <FormControl>
          <RadioGroup
            row
            aria-labelledby="work-setup-radio-group"
            name="work-setup-radio-group"
            value={workSetup}
            onChange={handleWorkSetupChange}
          >
            <FormControlLabel value="WFH" control={<Radio />} label="WFH" />
            <FormControlLabel
              sx={{ pl: 3 }}
              value="Onsite"
              control={<Radio />}
              label="On-site"
            />
            <FormControlLabel
              sx={{ pl: 3 }}
              value="Deployed"
              control={<Radio />}
              label="Deployed"
            />
          </RadioGroup>
        </FormControl>
      </Box>

      {workSetup === 'Deployed' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* No minTime: a Deployed shift may cross midnight, so any time of
                day is a valid lunch start. The value is sent as a bare HH:mm and
                the backend anchors it to the check-in, rolling to the next day
                when it falls at or before check-in time. */}
            <TimePicker
              label="Lunch break start"
              ampm
              value={lunchStartDate}
              onChange={(newValue) => {
                setLunchStartDate(newValue);
                setError('');
              }}
              renderInput={(params) => (
                <TextField {...params} InputLabelProps={{ shrink: true }} />
              )}
            />
          </LocalizationProvider>
        </Box>
      )}

      {/* ALERT MESSAGE */}
      {error && (
        <Alert
          severity="error"
          variant="filled"
          sx={{
            bgcolor: 'background.paper',
            mb: 2,
            pl: 1,
            py: 0,
            mt: 0,
            justifyContent: 'left',
          }}
        >
          {error}
        </Alert>
      )}

      {props.response && (
        <Alert
          severity={props.response.type || 'info'}
          variant="filled"
          sx={{
            bgcolor: 'background.paper',
            mb: 2,
            pl: 1,
            py: 0,
            mt: 0,
            justifyContent: 'left',
          }}
        >
          {props.response.text}
        </Alert>
      )}

      <ModalButton
        primaryClickHandler={handleConfirm}
        secondaryClickHandler={handleCancel}
        primaryBtnName={isSubmitting ? 'Processing...' : 'Confirm'}
        secondaryBtnName="Cancel"
        primaryStyle={{
          ...props.primaryStyle,
          opacity: loading || isSubmitting ? 0.6 : 1,
          cursor: loading || isSubmitting ? 'not-allowed' : 'pointer',
        }}
        disabled={loading || isSubmitting}
      />
    </GeneralModal>
  );
};
